import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { type MetaFunction } from 'react-router'

export const handle: SEOHandle = {
	getSitemapEntries: () => [{ route: '/legal-notice/privacy', priority: 0.7 }],
}

export const meta: MetaFunction = ({ location }) => {
	const title = `Privacy Policy | Legal Notice | ${import.meta.env.VITE_APP_TITLE}`
	const description = `Privacy Policy for ${import.meta.env.VITE_APP_TITLE} users`

	const url = `https://www.trailviewr.com${location.pathname}`

	return [
		{ title },
		{ name: 'description', content: description },
		{ tagName: 'link', rel: 'canonical', href: url },

		{ property: 'og:title', content: title },
		{ property: 'og:description', content: description },
		{ property: 'og:url', content: url },

		{ name: 'twitter:title', content: title },
		{ name: 'twitter:description', content: description },
		{ name: 'twitter:url', content: url },

		{
			'script:ld+json': {
				'@context': 'https://schema.org',
				'@type': 'WebPage',
				name: title,
				description,
				url,
			},
		},
	]
}

export default function PrivacyRoute() {
	return (
		<main className="mx-auto max-w-4xl px-4 py-8">
			<h1 className="mb-4 text-3xl font-bold">PRIVACY POLICY</h1>
			<p className="mb-4 font-semibold">Last updated October 6, 2025</p>
			<p className="mb-4">
				This Privacy Notice for Knight Rider Consulting, Inc. (doing business as
				TrailViewr) ("<strong>we</strong>," "<strong>us</strong>," or "
				<strong>our</strong>"), describes how and why we might access, collect,
				store, use, and/or share ("<strong>process</strong>") your personal
				information when you use our services ("<strong>Services</strong>"),
				including when you:
			</p>
			<ul className="mb-4 list-disc pl-6">
				<li className="mb-2">
					<p>
						Visit our website at{' '}
						<a href="/" className="text-blue-600 hover:underline">
							https://www.trailviewr.com
						</a>
						, or any website of ours that links to this Privacy Notice
					</p>
				</li>
				<li className="mb-2">
					<p>
						Engage with us in other related ways, including any sales,
						marketing, or events
					</p>
				</li>
			</ul>
			<p className="mb-4">
				<strong>Questions or concerns?</strong> Reading this Privacy Notice will
				help you understand your privacy rights and choices. We are responsible
				for making decisions about how your personal information is processed.
				If you do not agree with our policies and practices, please do not use
				our Services. If you still have any questions or concerns, please
				contact us at privacy@trailviewr.com.
			</p>
			<h2 id="summary-of-key-points" className="mb-4 text-2xl font-bold">
				SUMMARY OF KEY POINTS
			</h2>
			<p className="mb-4">
				<strong>
					<em>
						This summary provides key points from our Privacy Notice, but you
						can find out more details about any of these topics by clicking the
						link following each key point or by using our
					</em>
				</strong>

				<a href="#table-of-contents" className="text-blue-600 hover:underline">
					<strong>
						<em>table of contents</em>
					</strong>
				</a>

				<strong>
					<em> below to find the section you are looking for.</em>
				</strong>
			</p>
			<p className="mb-4">
				<strong>What personal information do we process?</strong> When you
				visit, use, or navigate our Services, we may process personal
				information depending on how you interact with us and the Services, the
				choices you make, and the products and features you use. Learn more
				about
				<a
					href="#what-information-do-we-collect"
					className="text-blue-600 hover:underline"
				>
					personal information you disclose to us
				</a>
				.
			</p>
			<p className="mb-4">
				<strong>Do we process any sensitive personal information?</strong> Some
				of the information may be considered "special" or "sensitive" in certain
				jurisdictions, for example your racial or ethnic origins, sexual
				orientation, and religious beliefs. We do not process sensitive personal
				information.
			</p>
			<p className="mb-4">
				<strong>Do we collect any information from third parties?</strong> We do
				not collect any information from third parties.
			</p>
			<p className="mb-4">
				<strong>How do we process your information?</strong> We process your
				information to provide, improve, and administer our Services,
				communicate with you, for security and fraud prevention, and to comply
				with law. We may also process your information for other purposes with
				your consent. We process your information only when we have a valid
				legal reason to do so. Learn more about
				<a
					href="#how-do-we-process-your-information"
					className="text-blue-600 hover:underline"
				>
					how we process your information
				</a>
				.
			</p>
			<p className="mb-4">
				<strong>
					In what situations and with which parties do we share personal
					information?
				</strong>
				We may share information in specific situations and with specific third
				parties. Learn more about
				<a
					href="#when-and-with-whom-do-we-share-your-personal-information"
					className="text-blue-600 hover:underline"
				>
					when and with whom we share your personal information
				</a>
				.
			</p>
			<p className="mb-4">
				<strong>What are your rights?</strong> Depending on where you are
				located geographically, the applicable privacy law may mean you have
				certain rights regarding your personal information. Learn more about
				<a
					href="#what-are-your-privacy-rights"
					className="text-blue-600 hover:underline"
				>
					your privacy rights
				</a>
				.
			</p>
			<p className="mb-4">
				<strong>How do you exercise your rights?</strong> The easiest way to
				exercise your rights is by submitting a
				<a
					href="/legal-notice/data-subject-access-request"
					className="text-blue-600 hover:underline"
				>
					data subject access request
				</a>
				, or by contacting us. We will consider and act upon any request in
				accordance with applicable data protection laws.
			</p>
			<p className="mb-4">
				Want to learn more about what we do with any information we collect?
				<a href="#table-of-contents" className="text-blue-600 hover:underline">
					Review the Privacy Notice in full
				</a>
				.
			</p>
			<h2 id="table-of-contents" className="mb-4 text-2xl font-bold">
				TABLE OF CONTENTS
			</h2>
			<ul className="mb-4 list-decimal pl-6">
				<li className="mb-2">
					<a
						href="#what-information-do-we-collect"
						className="text-blue-600 hover:underline"
					>
						WHAT INFORMATION DO WE COLLECT?
					</a>
				</li>
				<li className="mb-2">
					<a
						href="#how-do-we-process-your-information"
						className="text-blue-600 hover:underline"
					>
						HOW DO WE PROCESS YOUR INFORMATION?
					</a>
				</li>
				<li className="mb-2">
					<a
						href="#what-legal-bases-do-we-rely-on-to-process-your-personal-information"
						className="text-blue-600 hover:underline"
					>
						WHAT LEGAL BASES DO WE RELY ON TO PROCESS YOUR PERSONAL INFORMATION?
					</a>
				</li>
				<li className="mb-2">
					<a
						href="#when-and-with-whom-do-we-share-your-personal-information"
						className="text-blue-600 hover:underline"
					>
						WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?
					</a>
				</li>
				<li className="mb-2">
					<a
						href="#what-is-our-stance-on-third-party-websites"
						className="text-blue-600 hover:underline"
					>
						WHAT IS OUR STANCE ON THIRD-PARTY WEBSITES?
					</a>
				</li>
				<li className="mb-2">
					<a
						href="#do-we-use-cookies-and-other-tracking-technologies"
						className="text-blue-600 hover:underline"
					>
						DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?
					</a>
				</li>
				<li className="mb-2">
					<a
						href="#how-do-we-handle-your-social-logins"
						className="text-blue-600 hover:underline"
					>
						HOW DO WE HANDLE YOUR SOCIAL LOGINS?
					</a>
				</li>
				<li className="mb-2">
					<a
						href="#is-your-information-transferred-internationally"
						className="text-blue-600 hover:underline"
					>
						IS YOUR INFORMATION TRANSFERRED INTERNATIONALLY?
					</a>
				</li>
				<li className="mb-2">
					<a
						href="#how-long-do-we-keep-your-information"
						className="text-blue-600 hover:underline"
					>
						HOW LONG DO WE KEEP YOUR INFORMATION?
					</a>
				</li>
				<li className="mb-2">
					<a
						href="#do-we-collect-information-from-minors"
						className="text-blue-600 hover:underline"
					>
						DO WE COLLECT INFORMATION FROM MINORS?
					</a>
				</li>
				<li className="mb-2">
					<a
						href="#what-are-your-privacy-rights"
						className="text-blue-600 hover:underline"
					>
						WHAT ARE YOUR PRIVACY RIGHTS?
					</a>
				</li>
				<li className="mb-2">
					<a
						href="#controls-for-do-not-track-features"
						className="text-blue-600 hover:underline"
					>
						CONTROLS FOR DO-NOT-TRACK FEATURES
					</a>
				</li>
				<li className="mb-2">
					<a
						href="#do-united-states-residents-have-specific-privacy-rights"
						className="text-blue-600 hover:underline"
					>
						DO UNITED STATES RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?
					</a>
				</li>
				<li className="mb-2">
					<a
						href="#do-other-regions-have-specific-privacy-rights"
						className="text-blue-600 hover:underline"
					>
						DO OTHER REGIONS HAVE SPECIFIC PRIVACY RIGHTS?
					</a>
				</li>
				<li className="mb-2">
					<a
						href="#do-we-make-updates-to-this-notice"
						className="text-blue-600 hover:underline"
					>
						DO WE MAKE UPDATES TO THIS NOTICE?
					</a>
				</li>
				<li className="mb-2">
					<a
						href="#how-can-you-contact-us-about-this-notice"
						className="text-blue-600 hover:underline"
					>
						HOW CAN YOU CONTACT US ABOUT THIS NOTICE?
					</a>
				</li>
				<li className="mb-2">
					<a
						href="#how-can-you-review-update-or-delete-the-data-we-collect-from-you"
						className="text-blue-600 hover:underline"
					>
						HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?
					</a>
				</li>
			</ul>
			<h2
				id="what-information-do-we-collect"
				className="mb-4 text-2xl font-bold"
			>
				1. WHAT INFORMATION DO WE COLLECT?
			</h2>
			<h3 className="mb-2 text-xl font-semibold">
				Personal information you disclose to us
			</h3>
			<p className="mb-4">
				<strong>
					<em>In Short:</em>
				</strong>
				<em>We collect personal information that you provide to us.</em>
			</p>
			<p className="mb-4">
				We collect personal information that you voluntarily provide to us when
				you register on the Services, express an interest in obtaining
				information about us or our products and Services, when you participate
				in activities on the Services, or otherwise when you contact us.
			</p>
			<p className="mb-4">
				<strong>Personal Information Provided by You.</strong> The personal
				information that we collect depends on the context of your interactions
				with us and the Services, the choices you make, and the products and
				features you use. The personal information we collect may include the
				following:
			</p>
			<ul className="mb-4 list-disc pl-6">
				<li className="mb-2">names</li>
				<li className="mb-2">email addresses</li>
				<li className="mb-2">usernames</li>
				<li className="mb-2">passwords</li>
				<li className="mb-2">contact or authentication data</li>
				<li className="mb-2">debit/credit card numbers</li>
				<li className="mb-2">billing addresses</li>
				<li className="mb-2">phone numbers</li>
			</ul>
			<p className="mb-4">
				<strong>Sensitive Information.</strong> We do not process sensitive
				information.
			</p>
			<p className="mb-4">
				<strong>Payment Data.</strong> We may collect data necessary to process
				your payment if you choose to make purchases, such as your payment
				instrument number, and the security code associated with your payment
				instrument. All payment data is handled and stored by Stripe. You may
				find their privacy notice link(s) here:
				<a
					href="https://stripe.com/gb/legal/privacy-center"
					className="text-blue-600 hover:underline"
				>
					https://stripe.com/gb/legal/privacy-center
				</a>
				.
			</p>
			<p className="mb-4">
				<strong>Social Media Login Data.</strong> We may provide you with the
				option to register with us using your existing social media account
				details, like your Facebook, X, or other social media account. If you
				choose to register in this way, we will collect certain profile
				information about you from the social media provider, as described in
				the section called "
				<a
					href="#how-do-we-handle-your-social-logins"
					className="text-blue-600 hover:underline"
				>
					HOW DO WE HANDLE YOUR SOCIAL LOGINS?
				</a>
				" below.
			</p>
			<p className="mb-4">
				All personal information that you provide to us must be true, complete,
				and accurate, and you must notify us of any changes to such personal
				information.
			</p>
			<h3 className="mb-2 text-xl font-semibold">
				Information automatically collected
			</h3>
			<p className="mb-4">
				<strong>
					<em>In Short:</em>
				</strong>

				<em>
					Some information — such as your Internet Protocol (IP) address and/or
					browser and device characteristics — is collected automatically when
					you visit our Services.
				</em>
			</p>
			<p className="mb-4">
				We automatically collect certain information when you visit, use, or
				navigate the Services. This information does not reveal your specific
				identity (like your name or contact information) but may include device
				and usage information, such as your IP address, browser and device
				characteristics, operating system, language preferences, referring URLs,
				device name, country, location, information about how and when you use
				our Services, and other technical information. This information is
				primarily needed to maintain the security and operation of our Services,
				and for our internal analytics and reporting purposes.
			</p>
			<p className="mb-4">
				Like many businesses, we also collect information through cookies and
				similar technologies.
			</p>
			<p className="mb-4">The information we collect includes:</p>
			<ul className="mb-4 list-disc pl-6">
				<li className="mb-2">
					<p>
						<em>Log and Usage Data.</em> Log and usage data is service-related,
						diagnostic, usage, and performance information our servers
						automatically collect when you access or use our Services and which
						we record in log files. Depending on how you interact with us, this
						log data may include your IP address, device information, browser
						type, and settings and information about your activity in the
						Services (such as the date/time stamps associated with your usage,
						pages and files viewed, searches, and other actions you take such as
						which features you use), device event information (such as system
						activity, error reports (sometimes called "crash dumps"), and
						hardware settings).
					</p>
				</li>
				<li className="mb-2">
					<p>
						<em>Device Data.</em> We collect device data such as information
						about your computer, phone, tablet, or other device you use to
						access the Services. Depending on the device used, this device data
						may include information such as your IP address (or proxy server),
						device and application identification numbers, location, browser
						type, hardware model, Internet service provider and/or mobile
						carrier, operating system, and system configuration information.
					</p>
				</li>
				<li className="mb-2">
					<p>
						<em>Location Data.</em> We collect location data such as information
						about your device's location, which can be either precise or
						imprecise. How much information we collect depends on the type and
						settings of the device you use to access the Services. For example,
						we may use GPS and other technologies to collect geolocation data
						that tells us your current location (based on your IP address). You
						can opt out of allowing us to collect this information either by
						refusing access to the information or by disabling your Location
						setting on your device. However, if you choose to opt out, you may
						not be able to use certain aspects of the Services.
					</p>
				</li>
			</ul>
			<h3 className="mb-2 text-xl font-semibold">Google API</h3>
			<p className="mb-4">
				Our use of information received from Google APIs will adhere to
				<a
					href="https://developers.google.com/terms/api-services-user-data-policy"
					className="text-blue-600 hover:underline"
				>
					Google API Services User Data Policy
				</a>
				, including the
				<a
					href="https://developers.google.com/terms/api-services-user-data-policy#limited-use"
					className="text-blue-600 hover:underline"
				>
					Limited Use requirements
				</a>
				.
			</p>
			<h2
				id="how-do-we-process-your-information"
				className="mb-4 text-2xl font-bold"
			>
				2. HOW DO WE PROCESS YOUR INFORMATION?
			</h2>
			<p className="mb-4">
				<strong>
					<em>In Short:</em>
				</strong>

				<em>
					We process your information to provide, improve, and administer our
					Services, communicate with you, for security and fraud prevention, and
					to comply with law. We may also process your information for other
					purposes with your consent.
				</em>
			</p>
			<p className="mb-4">
				<strong>
					We process your personal information for a variety of reasons,
					depending on how you interact with our Services, including:
				</strong>
			</p>
			<ul className="mb-4 list-disc pl-6">
				<li className="mb-2">
					<p>
						<strong>
							To facilitate account creation and authentication and otherwise
							manage user accounts.
						</strong>
						We may process your information so you can create and log in to your
						account, as well as keep your account in working order.
					</p>
				</li>
				<li className="mb-2">
					<p>
						<strong>
							To deliver and facilitate delivery of services to the user.
						</strong>
						We may process your information to provide you with the requested
						service.
					</p>
				</li>
				<li className="mb-2">
					<p>
						<strong>
							To respond to user inquiries/offer support to users.
						</strong>
						We may process your information to respond to your inquiries and
						solve any potential issues you might have with the requested
						service.
					</p>
				</li>
				<li className="mb-2">
					<p>
						<strong>To send administrative information to you.</strong> We may
						process your information to send you details about our products and
						services, changes to our terms and policies, and other similar
						information.
					</p>
				</li>
				<li className="mb-2">
					<p>
						<strong>To fulfill and manage your orders.</strong> We may process
						your information to fulfill and manage your orders, payments,
						returns, and exchanges made through the Services.
					</p>
				</li>
				<li className="mb-2">
					<p>
						<strong>To enable user-to-user communications.</strong> We may
						process your information if you choose to use any of our offerings
						that allow for communication with another user.
					</p>
				</li>
				<li className="mb-2">
					<p>
						<strong>To request feedback.</strong> We may process your
						information when necessary to request feedback and to contact you
						about your use of our Services.
					</p>
				</li>
				<li className="mb-2">
					<p>
						<strong>
							To send you marketing and promotional communications.
						</strong>
						We may process the personal information you send to us for our
						marketing purposes, if this is in accordance with your marketing
						preferences. You can opt out of our marketing emails at any time.
						For more information, see "
						<a
							href="#what-are-your-privacy-rights"
							className="text-blue-600 hover:underline"
						>
							WHAT ARE YOUR PRIVACY RIGHTS?
						</a>
						" below.
					</p>
				</li>
				<li className="mb-2">
					<p>
						<strong>To deliver targeted advertising to you.</strong> We may
						process your information to develop and display personalized content
						and advertising tailored to your interests, location, and more.
					</p>
				</li>
				<li className="mb-2">
					<p>
						<strong>To protect our Services.</strong> We may process your
						information as part of our efforts to keep our Services safe and
						secure, including fraud monitoring and prevention.
					</p>
				</li>
				<li className="mb-2">
					<p>
						<strong>To identify usage trends.</strong> We may process
						information about how you use our Services to better understand how
						they are being used so we can improve them.
					</p>
				</li>
				<li className="mb-2">
					<p>
						<strong>
							To determine the effectiveness of our marketing and promotional
							campaigns.
						</strong>
						We may process your information to better understand how to provide
						marketing and promotional campaigns that are most relevant to you.
					</p>
				</li>
				<li className="mb-2">
					<p>
						<strong>To save or protect an individual's vital interest.</strong>{' '}
						We may process your information when necessary to save or protect an
						individual's vital interest, such as to prevent harm.
					</p>
				</li>
			</ul>
			<h2
				id="what-legal-bases-do-we-rely-on-to-process-your-personal-information"
				className="mb-4 text-2xl font-bold"
			>
				3. WHAT LEGAL BASES DO WE RELY ON TO PROCESS YOUR INFORMATION?
			</h2>
			<p className="mb-4">
				<em>
					<strong>In Short:</strong> We only process your personal information
					when we believe it is necessary and we have a valid legal reason
					(i.e., legal basis) to do so under applicable law, like with your
					consent, to comply with laws, to provide you with services to enter
					into or fulfill our contractual obligations, to protect your rights,
					or to fulfill our legitimate business interests.
				</em>
			</p>
			<p className="mb-4">
				<em>
					<strong>
						If you are located in the EU or UK, this section applies to you.
					</strong>
				</em>
			</p>
			<p className="mb-4">
				The General Data Protection Regulation (GDPR) and UK GDPR require us to
				explain the valid legal bases we rely on in order to process your
				personal information. As such, we may rely on the following legal bases
				to process your personal information:
			</p>
			<ul className="mb-4 list-disc pl-6">
				<li className="mb-2">
					<p>
						<strong>Consent.</strong> We may process your information if you
						have given us permission (i.e., consent) to use your personal
						information for a specific purpose. You can withdraw your consent at
						any time. Learn more about
						<a
							href="#withdrawing-your-consent"
							className="text-blue-600 hover:underline"
						>
							withdrawing your consent
						</a>
						.
					</p>
				</li>
				<li className="mb-2">
					<p>
						<strong>Performance of a Contract.</strong> We may process your
						personal information when we believe it is necessary to fulfill our
						contractual obligations to you, including providing our Services or
						at your request prior to entering into a contract with you.
					</p>
				</li>
				<li className="mb-2">
					<p>
						<strong>Legitimate Interests.</strong> We may process your
						information when we believe it is reasonably necessary to achieve
						our legitimate business interests and those interests do not
						outweigh your interests and fundamental rights and freedoms. For
						example, we may process your personal information for some of the
						purposes described in order to:
					</p>
				</li>
				<li className="mb-2">
					<p>
						Send users information about special offers and discounts on our
						products and services
					</p>
				</li>
				<li className="mb-2">
					<p>
						Develop and display personalized and relevant advertising content
						for our users
					</p>
				</li>
				<li className="mb-2">
					<p>
						Analyze how our Services are used so we can improve them to engage
						and retain users
					</p>
				</li>
				<li className="mb-2">
					<p>Support our marketing activities</p>
				</li>
				<li className="mb-2">
					<p>Diagnose problems and/or prevent fraudulent activities</p>
				</li>
				<li className="mb-2">
					<p>
						Understand how our users use our products and services so we can
						improve user experience
					</p>
				</li>
				<li className="mb-2">
					<p>
						<strong>Legal Obligations.</strong> We may process your information
						where we believe it is necessary for compliance with our legal
						obligations, such as to cooperate with a law enforcement body or
						regulatory agency, exercise or defend our legal rights, or disclose
						your information as evidence in litigation in which we are involved.
					</p>
				</li>
				<li className="mb-2">
					<p>
						<strong>Vital Interests.</strong> We may process your information
						where we believe it is necessary to protect your vital interests or
						the vital interests of a third party, such as situations involving
						potential threats to the safety of any person.
					</p>
				</li>
			</ul>
			<p className="mb-4">
				<strong>
					<em>If you are located in Canada, this section applies to you.</em>
				</strong>
			</p>
			<p className="mb-4">
				We may process your information if you have given us specific permission
				(i.e., express consent) to use your personal information for a specific
				purpose, or in situations where your permission can be inferred (i.e.,
				implied consent). You can
				<a
					href="#withdrawing-your-consent"
					className="text-blue-600 hover:underline"
				>
					withdraw your consent
				</a>{' '}
				at any time.
			</p>
			<p className="mb-4">
				In some exceptional cases, we may be legally permitted under applicable
				law to process your information without your consent, including, for
				example:
			</p>
			<ul className="mb-4 list-disc pl-6">
				<li className="mb-2">
					<p>
						If collection is clearly in the interests of an individual and
						consent cannot be obtained in a timely way
					</p>
				</li>
				<li className="mb-2">
					<p>For investigations and fraud detection and prevention</p>
				</li>
				<li className="mb-2">
					<p>For business transactions provided certain conditions are met</p>
				</li>
				<li className="mb-2">
					<p>
						If it is contained in a witness statement and the collection is
						necessary to assess, process, or settle an insurance claim
					</p>
				</li>
				<li className="mb-2">
					<p>
						For identifying injured, ill, or deceased persons and communicating
						with next of kin
					</p>
				</li>
				<li className="mb-2">
					<p>
						If we have reasonable grounds to believe an individual has been, is,
						or may be victim of financial abuse
					</p>
				</li>
				<li className="mb-2">
					<p>
						If it is reasonable to expect collection and use with consent would
						compromise the availability or the accuracy of the information and
						the collection is reasonable for purposes related to investigating a
						breach of an agreement or a contravention of the laws of Canada or a
						province
					</p>
				</li>
				<li className="mb-2">
					<p>
						If disclosure is required to comply with a subpoena, warrant, court
						order, or rules of the court relating to the production of records
					</p>
				</li>
				<li className="mb-2">
					<p>
						If it was produced by an individual in the course of their
						employment, business, or profession and the collection is consistent
						with the purposes for which the information was produced
					</p>
				</li>
				<li className="mb-2">
					<p>
						If the collection is solely for journalistic, artistic, or literary
						purposes
					</p>
				</li>
				<li className="mb-2">
					<p>
						If the information is publicly available and is specified by the
						regulations
					</p>
				</li>
			</ul>
			<h2
				id="when-and-with-whom-do-we-share-your-personal-information"
				className="mb-4 text-2xl font-bold"
			>
				4. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?
			</h2>
			<p className="mb-4">
				<strong>
					<em>In Short:</em>
				</strong>

				<em>
					We may share information in specific situations described in this
					section and/or with the following third parties.
				</em>
			</p>
			<p className="mb-4">
				We may need to share your personal information in the following
				situations:
			</p>
			<ul className="mb-4 list-disc pl-6">
				<li className="mb-2">
					<p>
						<strong>Business Transfers.</strong> We may share or transfer your
						information in connection with, or during negotiations of, any
						merger, sale of company assets, financing, or acquisition of all or
						a portion of our business to another company.
					</p>
				</li>
				<li className="mb-2">
					<p>
						<strong>When we use Google Maps Platform APIs.</strong> We may share
						your information with certain Google Maps Platform APIs (e.g.,
						Google Maps API, Places API). Google Maps uses GPS, Wi-Fi, and cell
						towers to estimate your location. GPS is accurate to about 20
						meters, while Wi-Fi and cell towers help improve accuracy when GPS
						signals are weak, like indoors. This data helps Google Maps provide
						directions, but it is not always perfectly precise. We obtain and
						store on your device ("cache") your location. You may revoke your
						consent anytime by contacting us at the contact details provided at
						the end of this document.
					</p>
				</li>
				<li className="mb-2">
					<p>
						<strong>Affiliates.</strong> We may share your information with our
						affiliates, in which case we will require those affiliates to honor
						this Privacy Notice. Affiliates include our parent company and any
						subsidiaries, joint venture partners, or other companies that we
						control or that are under common control with us.
					</p>
				</li>
				<li className="mb-2">
					<p>
						<strong>Business Partners.</strong> We may share your information
						with our business partners to offer you certain products, services,
						or promotions.
					</p>
				</li>
				<li className="mb-2">
					<p>
						<strong>Other Users.</strong> When you share personal information
						(for example, by posting comments, contributions, or other content
						to the Services) or otherwise interact with public areas of the
						Services, such personal information may be viewed by all users and
						may be publicly made available outside the Services in perpetuity.
						If you interact with other users of our Services and register for
						our Services through a social network (such as Facebook), your
						contacts on the social network will see your name, profile photo,
						and descriptions of your activity. Similarly, other users will be
						able to view descriptions of your activity, communicate with you
						within our Services, and view your profile.
					</p>
				</li>
			</ul>
			<h2
				id="what-is-our-stance-on-third-party-websites"
				className="mb-4 text-2xl font-bold"
			>
				5. WHAT IS OUR STANCE ON THIRD-PARTY WEBSITES?
			</h2>
			<p className="mb-4">
				<strong className="font-semibold">
					<em>In Short:</em>
				</strong>
				<em className="ml-2">
					We are not responsible for the safety of any information that you
					share with third parties that we may link to or who advertise on our
					Services, but are not affiliated with, our Services.
				</em>
			</p>
			<p className="mb-4">
				The Services may link to third-party websites, online services, or
				mobile applications and/or contain advertisements from third parties
				that are not affiliated with us and which may link to other websites,
				services, or applications. Accordingly, we do not make any guarantee
				regarding any such third parties, and we will not be liable for any loss
				or damage caused by the use of such third-party websites, services, or
				applications. The inclusion of a link towards a third-party website,
				service, or application does not imply an endorsement by us. We cannot
				guarantee the safety and privacy of data you provide to any third-party
				websites. Any data collected by third parties is not covered by this
				Privacy Notice. We are not responsible for the content or privacy and
				security practices and policies of any third parties, including other
				websites, services, or applications that may be linked to or from the
				Services. You should review the policies of such third parties and
				contact them directly to respond to your questions.
			</p>
			<h2
				id="do-we-use-cookies-and-other-tracking-technologies"
				className="mb-4 text-2xl font-bold"
			>
				6. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?
			</h2>
			<p className="mb-4">
				<strong className="font-semibold">
					<em>In Short:</em>
				</strong>
				<em className="ml-2">
					We may use cookies and other tracking technologies to collect and
					store your information.
				</em>
			</p>
			<p className="mb-4">
				We may use cookies and similar tracking technologies (like web beacons
				and pixels) to gather information when you interact with our Services.
				Some online tracking technologies help us maintain the security of our
				Services and your account, prevent crashes, fix bugs, save your
				preferences, and assist with basic site functions.
			</p>
			<p className="mb-4">
				We also permit third parties and service providers to use online
				tracking technologies on our Services for analytics and advertising,
				including to help manage and display advertisements, to tailor
				advertisements to your interests, or to send abandoned shopping cart
				reminders (depending on your communication preferences). The third
				parties and service providers use their technology to provide
				advertising about products and services tailored to your interests which
				may appear either on our Services or on other websites.
			</p>
			<p className="mb-4">
				To the extent these online tracking technologies are deemed to be a
				"sale"/"sharing" (which includes targeted advertising, as defined under
				the applicable laws) under applicable US state laws, you can opt out of
				these online tracking technologies by submitting a request as described
				below under section "
				<a
					href="#do-united-states-residents-have-specific-privacy-rights"
					className="text-blue-600 hover:underline"
				>
					DO UNITED STATES RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?
				</a>
				"
			</p>
			<p className="mb-4">
				Specific information about how we use such technologies and how you can
				refuse certain cookies is set out in our Cookie Notice.
			</p>
			<h3 className="mb-3 text-xl font-semibold">Google Analytics</h3>
			<p className="mb-4">
				We may share your information with Google Analytics to track and analyze
				the use of the Services. The Google Analytics Advertising Features that
				we may use include: Remarketing with Google Analytics, Google Display
				Network Impressions Reporting and Google Analytics Demographics and
				Interests Reporting. To opt out of being tracked by Google Analytics
				across the Services, visit
				<a
					href="https://tools.google.com/dlpage/gaoptout"
					className="mx-1 text-blue-600 hover:underline"
				>
					https://tools.google.com/dlpage/gaoptout
				</a>
				. You can opt out of Google Analytics Advertising Features through
				<a
					href="https://adssettings.google.com/"
					className="mx-1 text-blue-600 hover:underline"
				>
					Ads Settings
				</a>{' '}
				and Ad Settings for mobile apps. Other opt out means include
				<a
					href="https://optout.networkadvertising.org/"
					className="mx-1 text-blue-600 hover:underline"
				>
					https://optout.networkadvertising.org/
				</a>
				and
				<a
					href="https://www.networkadvertising.org/mobile-choice"
					className="mx-1 text-blue-600 hover:underline"
				>
					https://www.networkadvertising.org/mobile-choice
				</a>
				. For more information on the privacy practices of Google, please visit
				the
				<a
					href="https://policies.google.com/privacy"
					className="mx-1 text-blue-600 hover:underline"
				>
					Google Privacy & Terms page
				</a>
				.
			</p>
			<h2
				id="how-do-we-handle-your-social-logins"
				className="mb-4 text-2xl font-bold"
			>
				7. HOW DO WE HANDLE YOUR SOCIAL LOGINS?
			</h2>
			<p className="mb-4">
				<strong className="font-semibold">
					<em>In Short:</em>
				</strong>
				<em className="ml-2">
					If you choose to register or log in to our Services using a social
					media account, we may have access to certain information about you.
				</em>
			</p>
			<p className="mb-4">
				Our Services offer you the ability to register and log in using your
				third-party social media account details (like your Facebook or X
				logins). Where you choose to do this, we will receive certain profile
				information about you from your social media provider. The profile
				information we receive may vary depending on the social media provider
				concerned, but will often include your name, email address, friends
				list, and profile picture, as well as other information you choose to
				make public on such a social media platform.
			</p>
			<p className="mb-4">
				We will use the information we receive only for the purposes that are
				described in this Privacy Notice or that are otherwise made clear to you
				on the relevant Services. Please note that we do not control, and are
				not responsible for, other uses of your personal information by your
				third-party social media provider. We recommend that you review their
				privacy notice to understand how they collect, use, and share your
				personal information, and how you can set your privacy preferences on
				their sites and apps.
			</p>
			<h2
				id="is-your-information-transferred-internationally"
				className="mb-4 text-2xl font-bold"
			>
				8. IS YOUR INFORMATION TRANSFERRED INTERNATIONALLY?
			</h2>
			<p className="mb-4">
				<strong className="font-semibold">
					<em>In Short:</em>
				</strong>
				<em className="ml-2">
					We may transfer, store, and process your information in countries
					other than your own.
				</em>
			</p>
			<p className="mb-4">
				Our servers are located in the United States. If you are accessing our
				Services from outside the United States, please be aware that your
				information may be transferred to, stored by, and processed by us in our
				facilities and in the facilities of the third parties with whom we may
				share your personal information (see "
				<a href="#whoshare" className="text-blue-600 hover:underline">
					WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?
				</a>
				" above), in the United States, and other countries.
			</p>
			<p className="mb-4">
				If you are a resident in the European Economic Area (EEA), United
				Kingdom (UK), or Switzerland, then these countries may not necessarily
				have data protection laws or other similar laws as comprehensive as
				those in your country. However, we will take all necessary measures to
				protect your personal information in accordance with this Privacy Notice
				and applicable law.
			</p>
			<p className="mb-2 font-semibold">
				European Commission's Standard Contractual Clauses:
			</p>
			<p className="mb-4">
				We have implemented measures to protect your personal information,
				including by using the European Commission's Standard Contractual
				Clauses for transfers of personal information between our group
				companies and between us and our third-party providers. These clauses
				require all recipients to protect all personal information that they
				process originating from the EEA or UK in accordance with European data
				protection laws and regulations. Our Standard Contractual Clauses can be
				provided upon request. We have implemented similar appropriate
				safeguards with our third-party service providers and partners and
				further details can be provided upon request.
			</p>
			<h2 className="mb-4 text-2xl font-bold">
				9. HOW LONG DO WE KEEP YOUR INFORMATION?
			</h2>
			<p className="mb-4">
				<strong className="font-semibold">
					<em>In Short:</em>
				</strong>
				<em className="ml-2">
					We keep your information for as long as necessary to fulfill the
					purposes outlined in this Privacy Notice unless otherwise required by
					law.
				</em>
			</p>
			<p className="mb-4">
				We will only keep your personal information for as long as it is
				necessary for the purposes set out in this Privacy Notice, unless a
				longer retention period is required or permitted by law (such as tax,
				accounting, or other legal requirements). No purpose in this notice will
				require us keeping your personal information for longer than twelve (12)
				months past the termination of the user's account.
			</p>
			<p className="mb-4">
				When we have no ongoing legitimate business need to process your
				personal information, we will either delete or anonymize such
				information, or, if this is not possible (for example, because your
				personal information has been stored in backup archives), then we will
				securely store your personal information and isolate it from any further
				processing until deletion is possible.
			</p>
			<h2 className="mb-4 text-2xl font-bold">
				10. DO WE COLLECT INFORMATION FROM MINORS?
			</h2>
			<p className="mb-4">
				<strong className="font-semibold">
					<em>In Short:</em>
				</strong>
				<em className="ml-2">
					We do not knowingly collect data from or market to children under 18
					years of age.
				</em>
			</p>
			<p className="mb-4">
				We do not knowingly collect, solicit data from, or market to children
				under 18 years of age, nor do we knowingly sell such personal
				information. By using the Services, you represent that you are at least
				18 or that you are the parent or guardian of such a minor and consent to
				such minor dependent's use of the Services. If we learn that personal
				information from users less than 18 years of age has been collected, we
				will deactivate the account and take reasonable measures to promptly
				delete such data from our records. If you become aware of any data we
				may have collected from children under age 18, please contact us at
				<a
					href="mailto:privacy@trailviewr.com"
					className="ml-1 text-blue-600 hover:underline"
				>
					privacy@trailviewr.com
				</a>
				.
			</p>
			<h2 className="mb-4 text-2xl font-bold">
				11. WHAT ARE YOUR PRIVACY RIGHTS?
			</h2>
			<p className="mb-4">
				<strong className="font-semibold">
					<em>In Short:</em>
				</strong>
				<em className="ml-2">
					Depending on your state of residence in the US or in some regions,
					such as the European Economic Area (EEA), United Kingdom (UK),
					Switzerland, and Canada, you have rights that allow you greater access
					to and control over your personal information. You may review, change,
					or terminate your account at any time, depending on your country,
					province, or state of residence.
				</em>
			</p>
			<p className="mb-4">
				In some regions (like the EEA, UK, Switzerland, and Canada), you have
				certain rights under applicable data protection laws. These may include
				the right (i) to request access and obtain a copy of your personal
				information, (ii) to request rectification or erasure; (iii) to restrict
				the processing of your personal information; (iv) if applicable, to data
				portability; and (v) not to be subject to automated decision-making. In
				certain circumstances, you may also have the right to object to the
				processing of your personal information. You can make such a request by
				contacting us by using the contact details provided in the section "
				<a href="#contact" className="text-blue-600 hover:underline">
					HOW CAN YOU CONTACT US ABOUT THIS NOTICE?
				</a>
				" below.
			</p>
			<p className="mb-4">
				We will consider and act upon any request in accordance with applicable
				data protection laws.
			</p>
			<p className="mb-4">
				If you are located in the EEA or UK and you believe we are unlawfully
				processing your personal information, you also have the right to
				complain to your
				<a
					href="https://ec.europa.eu/justice/data-protection/bodies/authorities/index_en.htm"
					className="mx-1 text-blue-600 hover:underline"
				>
					Member State data protection authority
				</a>
				or
				<a
					href="https://ico.org.uk/make-a-complaint/data-protection-complaints/data-protection-complaints/"
					className="ml-1 text-blue-600 hover:underline"
				>
					UK data protection authority
				</a>
				.
			</p>
			<p className="mb-4">
				If you are located in Switzerland, you may contact the
				<a
					href="https://www.edoeb.admin.ch/edoeb/en/home.html"
					className="ml-1 text-blue-600 hover:underline"
				>
					Federal Data Protection and Information Commissioner
				</a>
				.
			</p>
			<p className="mb-4">
				<strong className="font-semibold">Withdrawing your consent:</strong> If
				we are relying on your consent to process your personal information,
				which may be express and/or implied consent depending on the applicable
				law, you have the right to withdraw your consent at any time. You can
				withdraw your consent at any time by contacting us by using the contact
				details provided in the section "
				<a href="#contact" className="text-blue-600 hover:underline">
					HOW CAN YOU CONTACT US ABOUT THIS NOTICE?
				</a>
				" below.
			</p>
			<p className="mb-4">
				However, please note that this will not affect the lawfulness of the
				processing before its withdrawal nor, when applicable law allows, will
				it affect the processing of your personal information conducted in
				reliance on lawful processing grounds other than consent.
			</p>
			<p className="mb-4">
				<strong className="font-semibold">
					Opting out of marketing and promotional communications:
				</strong>
				You can unsubscribe from our marketing and promotional communications at
				any time by clicking on the unsubscribe link in the emails that we send,
				replying "STOP" or "UNSUBSCRIBE" to the SMS messages that we send, or by
				contacting us using the details provided in the section "
				<a href="#contact" className="text-blue-600 hover:underline">
					HOW CAN YOU CONTACT US ABOUT THIS NOTICE?
				</a>
				" below. You will then be removed from the marketing lists. However, we
				may still communicate with you — for example, to send you
				service-related messages that are necessary for the administration and
				use of your account, to respond to service requests, or for other
				non-marketing purposes.
			</p>
			<h3 className="mb-2 text-xl font-semibold">Account Information</h3>
			<p className="mb-4">
				If you would at any time like to review or change the information in
				your account or terminate your account, you can:
			</p>
			<ul className="mb-4 list-disc pl-6">
				<li>
					<p>Log in to your account settings and update your user account.</p>
				</li>
				<li>
					<p>Contact us using the contact information provided.</p>
				</li>
			</ul>
			<p className="mb-4">
				Upon your request to terminate your account, we will deactivate or
				delete your account and information from our active databases. However,
				we may retain some information in our files to prevent fraud,
				troubleshoot problems, assist with any investigations, enforce our legal
				terms and/or comply with applicable legal requirements.
			</p>
			<p className="mb-4">
				<strong className="font-semibold">
					Cookies and similar technologies:
				</strong>{' '}
				Most Web browsers are set to accept cookies by default. If you prefer,
				you can usually choose to set your browser to remove cookies and to
				reject cookies. If you choose to remove cookies or reject cookies, this
				could affect certain features or services of our Services. You may also
				<a
					href="https://www.aboutads.info/choices/"
					className="mx-1 text-blue-600 hover:underline"
				>
					opt out of interest-based advertising by advertisers
				</a>
				on our Services.
			</p>
			<p className="mb-4">
				If you have questions or comments about your privacy rights, you may
				email us at privacy@trailviewr.com.
			</p>
			<h2 className="mb-4 text-2xl font-bold">
				12. CONTROLS FOR DO-NOT-TRACK FEATURES
			</h2>
			<p className="mb-4">
				Most web browsers and some mobile operating systems and mobile
				applications include a Do-Not-Track ("DNT") feature or setting you can
				activate to signal your privacy preference not to have data about your
				online browsing activities monitored and collected. At this stage, no
				uniform technology standard for recognizing and implementing DNT signals
				has been finalized. As such, we do not currently respond to DNT browser
				signals or any other mechanism that automatically communicates your
				choice not to be tracked online. If a standard for online tracking is
				adopted that we must follow in the future, we will inform you about that
				practice in a revised version of this Privacy Notice.
			</p>
			<p className="mb-4">
				California law requires us to let you know how we respond to web browser
				DNT signals. Because there currently is not an industry or legal
				standard for recognizing or honoring DNT signals, we do not respond to
				them at this time.
			</p>
			<h2 className="mb-4 text-2xl font-bold">
				13. DO UNITED STATES RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?
			</h2>
			<p className="mb-4 italic">
				<span className="font-semibold">In Short: </span>
				If you are a resident of California, Colorado, Connecticut, Delaware,
				Florida, Indiana, Iowa, Kentucky, Montana, New Hampshire, New Jersey,
				Oregon, Tennessee, Texas, Utah, or Virginia, you may have the right to
				request access to and receive details about the personal information we
				maintain about you and how we have processed it, correct inaccuracies,
				get a copy of, or delete your personal information. You may also have
				the right to withdraw your consent to our processing of your personal
				information. These rights may be limited in some circumstances by
				applicable law. More information is provided below.
			</p>

			<div>
				<h3 className="mb-2 text-xl font-semibold">
					Categories of Personal Information We Collect
				</h3>
				<p className="mb-4">
					We have collected the following categories of personal information in
					the past twelve (12) months:
				</p>
				<div className="space-y-4">
					{[
						{
							category: 'A. Identifiers',
							examples:
								'Contact details, such as real name, alias, postal address, telephone or mobile contact number, unique personal identifier, online identifier, Internet Protocol address, email address, and account name',
							collected: true,
						},
						{
							category:
								'B. Personal information as defined in the California Customer Records statute',
							examples:
								'Name, contact information, education, employment, employment history, and financial information',
							collected: false,
						},
						{
							category:
								'C. Protected classification characteristics under state or federal law',
							examples:
								'Gender, age, date of birth, race and ethnicity, national origin, marital status, and other demographic data',
							collected: false,
						},
						{
							category: 'D. Commercial information',
							examples:
								'Transaction information, purchase history, financial details, and payment information',
							collected: true,
						},
						{
							category: 'E. Biometric information',
							examples: 'Fingerprints and voiceprints',
							collected: true,
						},
						{
							category: 'F. Internet or other similar network activity',
							examples:
								'Browsing history, search history, online behavior, interest data, and interactions with our and other websites, applications, systems, and advertisements',
							collected: true,
						},
						{
							category: 'G. Geolocation data',
							examples: 'Device location',
							collected: true,
						},
						{
							category: 'H. Audio, electronic, sensory, or similar information',
							examples:
								'Images and audio, video or call recordings created in connection with our business activities',
							collected: true,
						},
						{
							category: 'I. Professional or employment-related information',
							examples:
								'Business contact details in order to provide you our Services at a business level or job title, work history, and professional qualifications if you apply for a job with us',
							collected: false,
						},
						{
							category: 'J. Education Information',
							examples: 'Student records and directory information',
							collected: false,
						},
						{
							category:
								'K. Inferences drawn from collected personal information',
							examples:
								"Inferences drawn from any of the collected personal information listed above to create a profile or summary about, for example, an individual's preferences and characteristics",
							collected: true,
						},
						{
							category: 'L. Sensitive personal Information',
							examples: '',
							collected: false,
						},
					].map((item, index) => (
						<div key={index} className="rounded-lg bg-gray-100 p-4">
							<h4 className="mb-2 font-semibold">{item.category}</h4>
							<p className="mb-2 text-sm">{item.examples}</p>
							<p className="text-sm font-semibold">
								Collected: {item.collected ? 'YES' : 'NO'}
							</p>
						</div>
					))}
				</div>
				<div>
					<p className="mb-4">
						We may also collect other personal information outside of these
						categories through instances where you interact with us in person,
						online, or by phone or mail in the context of:
					</p>
					<ul className="mb-4 list-disc space-y-2 pl-5">
						<li>Receiving help through our customer support channels;</li>
						<li>Participation in customer surveys or contests; and</li>
						<li>
							Facilitation in the delivery of our Services and to respond to
							your inquiries.
						</li>
					</ul>
				</div>

				<div>
					<p className="mb-4">
						We will use and retain the collected personal information as needed
						to provide the Services or for:
					</p>
					<ul className="mb-4 list-disc space-y-2 pl-5">
						{['A', 'D', 'E', 'F', 'G', 'H', 'K'].map((category) => (
							<li key={category}>
								Category {category} - As long as the user has an account with us
							</li>
						))}
					</ul>
				</div>

				<div className="space-y-4">
					<h3 className="text-xl font-semibold">
						Sources of Personal Information
					</h3>
					<p>
						Learn more about the sources of personal information we collect in "
						<a href="#infocollect" className="text-blue-600 hover:underline">
							WHAT INFORMATION DO WE COLLECT?
						</a>
						"
					</p>

					<h3 className="text-xl font-semibold">
						How We Use and Share Personal Information
					</h3>
					<p>
						Learn about how we use your personal information in the section, "
						<a href="#infouse" className="text-blue-600 hover:underline">
							HOW DO WE PROCESS YOUR INFORMATION?
						</a>
						"
					</p>

					<h3 className="text-xl font-semibold">
						Will your information be shared with anyone else?
					</h3>
					<p>
						We may disclose your personal information with our service providers
						pursuant to a written contract between us and each service provider.
						Learn more about how we disclose personal information to in the
						section, "
						<a href="#whoshare" className="text-blue-600 hover:underline">
							WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?
						</a>
						"
					</p>
					<p>
						We may use your personal information for our own business purposes,
						such as for undertaking internal research for technological
						development and demonstration. This is not considered to be
						"selling" of your personal information.
					</p>
					<p>
						We have not disclosed, sold, or shared any personal information to
						third parties for a business or commercial purpose in the preceding
						twelve (12) months. We will not sell or share personal information
						in the future belonging to website visitors, users, and other
						consumers.
					</p>
				</div>

				<h3 className="mb-4 text-xl font-semibold">Your Rights</h3>
				<p className="mb-4">
					You have rights under certain US state data protection laws. However,
					these rights are not absolute, and in certain cases, we may decline
					your request as permitted by law. These rights include:
				</p>
				<ul className="mb-4 list-disc space-y-2 pl-5">
					<li>
						<strong>Right to know</strong> whether or not we are processing your
						personal data
					</li>
					<li>
						<strong>Right to access</strong> your personal data
					</li>
					<li>
						<strong>Right to correct</strong> inaccuracies in your personal data
					</li>
					<li>
						<strong>Right to request</strong> the deletion of your personal data
					</li>
					<li>
						<strong>Right to obtain a copy</strong> of the personal data you
						previously shared with us
					</li>
					<li>
						<strong>Right to non-discrimination</strong> for exercising your
						rights
					</li>
					<li>
						<strong>Right to opt out</strong> of the processing of your personal
						data if it is used for targeted advertising (or sharing as defined
						under California's privacy law), the sale of personal data, or
						profiling in furtherance of decisions that produce legal or
						similarly significant effects ("profiling")
					</li>
				</ul>
				<p className="mb-4">
					Depending upon the state where you live, you may also have the
					following rights:
				</p>
				<ul className="mb-4 list-disc space-y-2 pl-5">
					<li>
						Right to obtain a list of the categories of third parties to which
						we have disclosed personal data (as permitted by applicable law,
						including California's and Delaware's privacy law)
					</li>
					<li>
						Right to obtain a list of specific third parties to which we have
						disclosed personal data (as permitted by applicable law, including
						Oregon's privacy law)
					</li>
					<li>
						Right to limit use and disclosure of sensitive personal data (as
						permitted by applicable law, including California's privacy law)
					</li>
					<li>
						Right to opt out of the collection of sensitive data and personal
						data collected through the operation of a voice or facial
						recognition feature (as permitted by applicable law, including
						Florida's privacy law)
					</li>
				</ul>
			</div>

			<h3 className="mb-4 text-xl font-semibold">
				How to Exercise Your Rights
			</h3>
			<p className="mb-4">
				To exercise these rights, you can contact us by submitting a
				<a
					href="/legal-notice/data-subject-access-request"
					className="mx-1 text-blue-600 hover:underline"
				>
					data subject access request
				</a>
				, by emailing us at privacy@trailviewr.com, or by referring to the
				contact details at the bottom of this document.
			</p>
			<p className="mb-4">
				Under certain US state data protection laws, you can designate an
				authorized agent to make a request on your behalf. We may deny a request
				from an authorized agent that does not submit proof that they have been
				validly authorized to act on your behalf in accordance with applicable
				laws.
			</p>

			<h3 className="mb-4 text-xl font-semibold">Request Verification</h3>
			<p className="mb-4">
				Upon receiving your request, we will need to verify your identity to
				determine you are the same person about whom we have the information in
				our system. We will only use personal information provided in your
				request to verify your identity or authority to make the request.
				However, if we cannot verify your identity from the information already
				maintained by us, we may request that you provide additional information
				for the purposes of verifying your identity and for security or
				fraud-prevention purposes.
			</p>
			<p className="mb-4">
				If you submit the request through an authorized agent, we may need to
				collect additional information to verify your identity before processing
				your request and the agent will need to provide a written and signed
				permission from you to submit such request on your behalf.
			</p>

			<h3 className="mb-4 text-xl font-semibold">Appeals</h3>
			<p className="mb-4">
				Under certain US state data protection laws, if we decline to take
				action regarding your request, you may appeal our decision by emailing
				us at privacy@trailviewr.com. We will inform you in writing of any
				action taken or not taken in response to the appeal, including a written
				explanation of the reasons for the decisions. If your appeal is denied,
				you may submit a complaint to your state attorney general.
			</p>

			<h3 className="mb-4 text-xl font-semibold">
				California "Shine The Light" Law
			</h3>
			<p className="mb-4">
				California Civil Code Section 1798.83, also known as the "Shine The
				Light" law, permits our users who are California residents to request
				and obtain from us, once a year and free of charge, information about
				categories of personal information (if any) we disclosed to third
				parties for direct marketing purposes and the names and addresses of all
				third parties with which we shared personal information in the
				immediately preceding calendar year. If you are a California resident
				and would like to make such a request, please submit your request in
				writing to us by using the contact details provided in the section "
				<a href="#contact" className="text-blue-600 hover:underline">
					HOW CAN YOU CONTACT US ABOUT THIS NOTICE?
				</a>
				"
			</p>
			<h2 className="mb-4 text-2xl font-bold">
				14. DO OTHER REGIONS HAVE SPECIFIC PRIVACY RIGHTS?
			</h2>
			<p className="mb-4">
				<em>
					<strong>In Short:</strong> You may have additional rights based on the
					country you reside in.
				</em>
			</p>
			<h3 className="mb-2 text-xl font-semibold">Australia and New Zealand</h3>
			<p className="mb-4">
				We collect and process your personal information under the obligations
				and conditions set by Australia's Privacy Act 1988 and New Zealand's
				Privacy Act 2020 (Privacy Act).
			</p>
			<p className="mb-4">
				This Privacy Notice satisfies the notice requirements defined in both
				Privacy Acts, in particular: what personal information we collect from
				you, from which sources, for which purposes, and other recipients of
				your personal information.
			</p>
			<p className="mb-2">
				If you do not wish to provide the personal information necessary to
				fulfill their applicable purpose, it may affect our ability to provide
				our services, in particular:
			</p>
			<ul className="mb-4 list-disc pl-6">
				<li>offer you the products or services that you want</li>
				<li>respond to or help with your requests</li>
				<li>manage your account with us</li>
				<li>confirm your identity and protect your account</li>
			</ul>
			<p className="mb-4">
				At any time, you have the right to request access to or correction of
				your personal information. You can make such a request by contacting us
				by using the contact details provided in the section "
				<a href="#request" className="text-blue-600 hover:underline">
					HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?
				</a>
				"
			</p>
			<p className="mb-4">
				If you believe we are unlawfully processing your personal information,
				you have the right to submit a complaint about a breach of the
				Australian Privacy Principles to the
				<a
					href="https://www.oaic.gov.au/privacy/privacy-complaints/lodge-a-privacy-complaint-with-us"
					className="mx-1 text-blue-600 hover:underline"
				>
					Office of the Australian Information Commissioner
				</a>
				and a breach of New Zealand's Privacy Principles to the
				<a
					href="https://www.privacy.org.nz/your-rights/making-a-complaint/"
					className="mx-1 text-blue-600 hover:underline"
				>
					Office of New Zealand Privacy Commissioner
				</a>
				.
			</p>
			<h3 className="mb-2 text-xl font-semibold">Republic of South Africa</h3>
			<p className="mb-4">
				At any time, you have the right to request access to or correction of
				your personal information. You can make such a request by contacting us
				by using the contact details provided in the section "
				<a href="#request" className="text-blue-600 hover:underline">
					HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?
				</a>
				"
			</p>
			<p className="mb-4">
				If you are unsatisfied with the manner in which we address any complaint
				with regard to our processing of personal information, you can contact
				the office of the regulator, the details of which are:
			</p>
			<p className="mb-2">
				<a
					href="https://inforegulator.org.za/"
					className="text-blue-600 hover:underline"
				>
					The Information Regulator (South Africa)
				</a>
			</p>
			<p className="mb-2">
				General enquiries:
				<a
					href="mailto:enquiries@inforegulator.org.za"
					className="ml-1 text-blue-600 hover:underline"
				>
					enquiries@inforegulator.org.za
				</a>
			</p>
			<p className="mb-4">
				Complaints (complete POPIA/PAIA form 5):
				<a
					href="mailto:PAIAComplaints@inforegulator.org.za"
					className="mx-1 text-blue-600 hover:underline"
				>
					PAIAComplaints@inforegulator.org.za
				</a>
				&
				<a
					href="mailto:POPIAComplaints@inforegulator.org.za"
					className="mx-1 text-blue-600 hover:underline"
				>
					POPIAComplaints@inforegulator.org.za
				</a>
			</p>
			<h2 className="mb-4 text-2xl font-bold">
				15. DO WE MAKE UPDATES TO THIS NOTICE?
			</h2>
			<p className="mb-4">
				<em>
					<strong>In Short:</strong> Yes, we will update this notice as
					necessary to stay compliant with relevant laws.
				</em>
			</p>
			<p className="mb-4">
				We may update this Privacy Notice from time to time. The updated version
				will be indicated by an updated "Revised" date at the top of this
				Privacy Notice. If we make material changes to this Privacy Notice, we
				may notify you either by prominently posting a notice of such changes or
				by directly sending you a notification. We encourage you to review this
				Privacy Notice frequently to be informed of how we are protecting your
				information.
			</p>
			<h2 className="mb-4 text-2xl font-bold">
				16. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?
			</h2>
			<p className="mb-4">
				If you have questions or comments about this notice, you may email us at
				<a
					href="mailto:privacy@trailviewr.com"
					className="mx-1 text-blue-600 hover:underline"
				>
					privacy@trailviewr.com
				</a>
				or contact us by post at:
			</p>
			<address className="mb-4 not-italic">
				Knight Rider Consulting, Inc.
				<br />
				5900 Balcones Drive # 19816
				<br />
				Austin, TX 78731
				<br />
				United States
			</address>
			<h2 className="mb-4 text-2xl font-bold">
				17. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?
			</h2>
			<p className="mb-4">
				Based on the applicable laws of your country or state of residence in
				the US, you may have the right to request access to the personal
				information we collect from you, details about how we have processed it,
				correct inaccuracies, or delete your personal information. You may also
				have the right to withdraw your consent to our processing of your
				personal information. These rights may be limited in some circumstances
				by applicable law. To request to review, update, or delete your personal
				information, please fill out and submit a
				<a
					href="/legal-notice/data-subject-access-request"
					className="mx-1 text-blue-600 hover:underline"
				>
					data subject access request
				</a>
				.
			</p>
		</main>
	)
}
